/**
 * ContentListBlockRenderer renders a content_list block using the
 * ContentListCard component from @strivelabs/ui-blocks.
 *
 * Used in both chat and report contexts via dispatchBlock().
 *
 * Per-item `chat_cta` (when present) is rendered into the card's footer slot
 * via the existing ChatCTABlockRenderer, which handles click → workflow trigger
 * → navigation with the per-item preview blocks and platform context.
 */

import { ContentListCard } from '@strivelabs/ui-blocks';
import type { BlockElement, UIBlock } from '@/types/ui-blocks';
import { ChatCTABlockRenderer } from './chat-cta-block-renderer';

export interface ContentListBlockRendererProps {
  block: UIBlock;
}

export function ContentListBlockRenderer({ block }: ContentListBlockRendererProps) {
  if (block.type !== 'content_list') return null;

  const { content } = block;

  return (
    <ContentListCard
      title={content.title}
      titleSize={content.title_size}
      items={content.items.map((item) => ({
        ...item,
        // Actions from backend don't have onClick — render as non-interactive labels
        actions: item.actions?.map((a) => ({
          label: a.label,
          variant: a.variant,
          onClick: () => {},
        })),
        footer: item.chat_cta ? (
          // Cross-package shape: registry's ChatCtaComponentPayload is structurally
          // compatible with platform's BlockElement at runtime, but the two packages
          // declare BlockBehaviour separately so TypeScript can't bridge them.
          <ChatCTABlockRenderer element={item.chat_cta as unknown as BlockElement} />
        ) : undefined,
      }))}
      showSteps={content.show_steps}
    />
  );
}
